/**
 * Utility functions for handling tee colors in the golf application
 */

// Map of tee colors to their hex values
const teeColorMap: Record<string, string> = {
  white: '#FFFFFF',
  black: '#000000',
  blue: '#0000FF',
  red: '#FF0000',
  yellow: '#FFD700',
  gold: '#FFD700',  // Alias for yellow
  purple: '#800080',
  orange: '#FFA500',
  green: '#008000'
};

/**
 * Get the CSS color value for a tee color
 * @param teeColor The tee color name
 * @returns The CSS color value
 */
export const getTeeColorValue = (teeColor: string): string => {
  return teeColorMap[teeColor.toLowerCase()] || '#CCCCCC';
};

/**
 * Get the appropriate text color for a tee background
 * @param teeColor The tee color name
 * @returns 'white' or 'black' depending on background contrast
 */
export const getTeeTextColor = (teeColor: string): 'white' | 'black' => {
  // Dark colors need white text, light colors need black text
  const darkColors = ['black', 'blue', 'purple', 'green'];
  return darkColors.includes(teeColor.toLowerCase()) ? 'white' : 'black';
};

/**
 * Create a styled tee color badge component
 * @param teeColor The tee color name
 * @param size Size in pixels (default: 16)
 * @returns JSX for the tee color badge
 */
const renderTeeBadge = (teeColor: string, size: number = 16) => {
  const bgColor = getTeeColorValue(teeColor);
  const textColor = getTeeTextColor(teeColor);
  
  return {
    backgroundColor: bgColor,
    color: textColor,
    borderColor: teeColor.toLowerCase() === 'white' ? '#CCCCCC' : bgColor,
    // We're not using the size parameter in the style object since we'll apply it via className
    // but we're keeping it as a parameter for future flexibility
    width: `${size}px`,
    height: `${size}px`
  };
};
